import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FunctionComponent, PropsWithChildren, Children, isValidElement, cloneElement, ReactElement } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styles from './ExpandableSelector.module.scss';

// Props for the main ExpandableSelector component
interface ExpandableSelectorProps extends PropsWithChildren {
    id: string;
    expanded: boolean;
    className?: string;
    dropdownAlign?: 'left' | 'right';
    childrenClassName?: string;
    title?: string;
    label?: string | React.ReactNode;
    disabled?: boolean;
    onExpand: () => void;
    onClickAway: () => void;
}

// Props interface for the List component to enable type-safe cloning
interface ListProps {
    onItemSelected?: () => void;
}

export const ExpandableSelector: FunctionComponent<ExpandableSelectorProps> = props => {
    // Clone children (ExpandableSelectorList) to inject the onItemSelected callback
    // This enables automatic closing of the selector when an item is selected
    const enhancedChildren = Children.map(props.children, child => {
        if (isValidElement<ListProps>(child)) {
            return cloneElement<ListProps>(child as ReactElement<ListProps>, {
                onItemSelected: props.onClickAway
            });
        }
        return child;
    });

    return (
        <div className={clsx(styles.container, props.className)}>
            {/* Optional title section */}
            {props.title && <div className={styles.title}>{props.title}</div>}
            <div className={styles.section}>
                {/* Main label/display value */}
                {props.label && <div className={styles.text}>{props.label}</div>}
                {/* Click away listener to handle outside clicks */}
                <ClickAwayListener onClickAway={props.onClickAway}>
                    <div className={styles.wrapper}>
                        {!props.disabled && (
                            <div
                                id={`expandable_selector_${props.id}`}
                                className={styles.expandButton}
                                onClick={props.onExpand}
                            >
                                <FontAwesomeIcon
                                    className={styles.icon}
                                    icon={props.expanded ? faChevronUp : faChevronDown}
                                />
                            </div>
                        )}
                        {props.expanded && (
                            <div
                                className={clsx(
                                    styles.content,
                                    props.childrenClassName,
                                    props.dropdownAlign === 'right' && styles.right
                                )}
                            >
                                {enhancedChildren}
                            </div>
                        )}
                    </div>
                </ClickAwayListener>
            </div>
        </div>
    );
};

export default ExpandableSelector;
